import { getActiveLanguage, t } from "../i18n.js";

/**
 * Shared formatting and DOM helpers for the high-score table's row + header cells
 * ({@code leaderboard-render.js}), across every surface the board renders on (game-start,
 * `/leaderboard`, the menu overlay). Neutral infrastructure — the name carries no cluster
 * ownership.
 *
 * The board's counts and sums (rounds played, score, Bilanz) render compact — three significant
 * digits plus a unit above 1000 (sig-digit-numbers task, Andre's 2026-08-24 call) — but this
 * module carries no name for that: `leaderboard-render.js` imports `formatCompact` /
 * `formatCompactSigned` from `../compact-numbers.js` directly. A local alias would be a second
 * name for one contract and exactly the hazard of picking the unsigned form where the signed one
 * belongs. `formatAvg` below stays here because it IS this cluster's own rule: an average per
 * round never reaches the magnitudes the compact form exists for, and its one fractional digit is
 * the reading.
 *
 * Locale-aware averages: German renders 12,3; English 12.3. Locale source is
 * `getActiveLanguage()` — the same active UI language the i18n labels are fetched in — so the
 * number separators and the label language stay in sync, including when the user overrides the
 * language via the footer selector. (Sourcing from `navigator.language` instead would split them:
 * an override to German on an English browser would show German labels but English-style
 * separators.)
 *
 * Built lazily on first format call, then cached: a language change reloads the page,
 * so one locale per page load is correct. Lazy (not a module-load const) so importing
 * this leaf doesn't pull `getActiveLanguage` at eval time — keeps the transitive
 * importers that only need `el` / `setName` (e.g. the result-screen tests) decoupled
 * from the i18n language API.
 */
let avgFmt;

function avgFormatter() {
  avgFmt ??= new Intl.NumberFormat(getActiveLanguage(), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return avgFmt;
}

export function formatAvg(value) {
  const rounded = Math.round(value * 10) / 10;
  const fmt = avgFormatter();
  if (rounded === 0) return fmt.format(0);
  const sign = rounded > 0 ? "+" : "−";
  return sign + fmt.format(Math.abs(rounded));
}

/**
 * Duration-taking relative-time core, in milliseconds — "just now" / "N minutes ago" / "N hours
 * ago" / "N days ago" / "N weeks ago", speaking through the `leaderboardRel*` i18n keys for both
 * locales (despite the name, the bucket thresholds were tuned against German phrasing, not the
 * keys being DE-only). Moved here from `leaderboard-render.js`'s former private
 * `formatRelativeGerman` (multiplayer waiting-state UX design § Structure — the leaderboard
 * package restructuring landed on main after this move, so the origin file was
 * `game-start/game-start-leaderboard-render.js` at extraction time) so a second, ms-native
 * consumer — the waiting line's recency ("Zuletzt aktiv vor 2 Stunden") — doesn't need to
 * fabricate two ISO timestamps just to reuse the bucket logic. `formatRelative` below is the
 * original ISO-pair wrapper, kept for the leaderboard row's own call shape. The one cross-package
 * consumer is `waiting-state.js` (js root) — it rides `api.js`'s eager ingest funnel and can't
 * afford this package's own lazy-loaded siblings, so it reaches in here instead.
 */
export function formatRelativeDuration(ms) {
  if (ms < 60_000) return t("leaderboardRelJustNow");
  if (ms < 3_600_000) {
    const m = Math.floor(ms / 60_000);
    return t(m === 1 ? "leaderboardRelMinute" : "leaderboardRelMinutes", m);
  }
  if (ms < 86_400_000) {
    const h = Math.floor(ms / 3_600_000);
    return t(h === 1 ? "leaderboardRelHour" : "leaderboardRelHours", h);
  }
  if (ms < 7 * 86_400_000) {
    const d = Math.floor(ms / 86_400_000);
    return t(d === 1 ? "leaderboardRelDay" : "leaderboardRelDays", d);
  }
  const w = Math.floor(ms / (7 * 86_400_000));
  return t(w === 1 ? "leaderboardRelWeek" : "leaderboardRelWeeks", w);
}

/** ISO-pair wrapper over {@link formatRelativeDuration} — the leaderboard row's own call shape
 *  (`leaderboard-render.js#buildRecencyLine`). */
export function formatRelative(thenIso, nowIso) {
  return formatRelativeDuration(Date.parse(nowIso) - Date.parse(thenIso));
}

/** XSS-safe text setter — always textContent, never innerHTML. */
export function setName(target, text) {
  target.textContent = text;
}

export function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}
